/**
 * TASK-12181 — destino do Encaminhar por Organização e fila (spec 2026-10-02 §7).
 *
 * Substitui o seletor de "time roteável": as opções vêm de
 * GET /v1/tenants/{tid}/workflow/{id}/encaminhar/destinos, que já devolve só as Organizações
 * que podem atender o tenant do chamado (fila interna só para quem o contém).
 */
import { Select } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '@/api/client'
import type { AtendimentoEscolhido, OrgQueAtende } from './AtendidoPor'

export interface LadoAtendimento {
  org_id: number
  org_nome: string | null
  fila_id: number | null
  fila_nome: string | null
}

export interface DestinosDoEncaminhar {
  atual: LadoAtendimento | null
  opcoes: OrgQueAtende[]
}

interface Props {
  tenantId: number
  chamadoId: number
  value: AtendimentoEscolhido | null
  onChange: (v: AtendimentoEscolhido | null) => void
  size?: 'xs' | 'sm'
  label?: string
}

/** Corpo do PATCH …/encaminhar para um destino escolhido aqui. */
export function corpoDoEncaminhar(destino: AtendimentoEscolhido, nota?: string) {
  return { attending_org_id: destino.attending_org_id, queue_id: destino.queue_id, note: nota || undefined }
}

export function DestinoDeAtendimento({ tenantId, chamadoId, value, onChange, size = 'sm', label = 'Para' }: Props) {
  const { data, isLoading } = useQuery<DestinosDoEncaminhar>({
    queryKey: ['chamado-encaminhar-destinos', tenantId, chamadoId],
    queryFn: async () =>
      (await api.get(`/v1/tenants/${tenantId}/workflow/${chamadoId}/encaminhar/destinos`)).data,
    staleTime: 60_000,
  })

  const atual = data?.atual ?? null
  const opcoes = useMemo(
    () => (data?.opcoes ?? []).map((o) => ({
      group: o.org_nome,
      items: o.filas.map((f) => ({
        value: `${o.org_id}:${f.fila_id}`,
        // A fila onde o chamado já está não é destino: encaminhar para ela não muda nada.
        disabled: atual?.org_id === o.org_id && atual?.fila_id === f.fila_id,
        label: f.padrao ? `${f.nome} (padrão)` : f.nome,
      })),
    })),
    [data, atual],
  )

  return (
    <Select
      size={size}
      label={label}
      placeholder={isLoading ? 'Carregando…' : 'Organização e fila'}
      data={opcoes}
      searchable
      nothingFoundMessage="Nenhuma Organização atende este tenant"
      value={value ? `${value.attending_org_id}:${value.queue_id}` : null}
      onChange={(v) => {
        if (!v) { onChange(null); return }
        const [org, fila] = v.split(':').map(Number)
        onChange({ attending_org_id: org, queue_id: fila })
      }}
    />
  )
}
